import React, { useEffect, useState, useCallback } from 'react';

const DEFAULT_DURATIONS = {
  info: 3000,
  success: 3000,
  warning: 4000,
  error: 5000,
};

const TOAST_ICONS = {
  success: '\u2713',  // checkmark
  error: '\u2717',    // X mark
  warning: '\u26A0',  // warning triangle
  info: '\u2139',     // info circle
};

const Toast = () => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail;

      // Support both legacy string format and new object format
      let toastData;
      if (typeof detail === 'string') {
        // Legacy format: just a message string
        toastData = { message: detail, type: 'info' };
      } else {
        // New format: { message, type, duration, retryAfter }
        toastData = detail || {};
      }

      const {
        message = '',
        type = 'info',
        duration,
        retryAfter,
      } = toastData;

      const id = Date.now() + Math.random();
      const effectiveDuration = duration || DEFAULT_DURATIONS[type] || 3000;

      // Build display message with retry-after if provided
      let displayMessage = message;
      if (retryAfter && type === 'error') {
        displayMessage = `${message} (retry in ${retryAfter}s)`;
      }

      const newToast = {
        id,
        message: displayMessage,
        type,
        duration: effectiveDuration,
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-dismiss after duration
      setTimeout(() => {
        removeToast(id);
      }, effectiveDuration);
    };

    window.addEventListener('app-toast', handler);
    return () => window.removeEventListener('app-toast', handler);
  }, [removeToast]);

  const handleDismiss = (id) => {
    removeToast(id);
  };

  if (toasts.length === 0) return null;

  return (
    <div className="lrbrain-toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`lrbrain-toast lrbrain-toast--${toast.type} show`}
          role="alert"
          aria-live="polite"
        >
          <span className="lrbrain-toast__icon">
            {TOAST_ICONS[toast.type] || TOAST_ICONS.info}
          </span>
          <span className="lrbrain-toast__message">{toast.message}</span>
          <button
            className="lrbrain-toast__dismiss"
            onClick={() => handleDismiss(toast.id)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
};

export default Toast;
